import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
	CircularProgress,
	TextField,
	FormLabel,
	FormControl,
	Select,
	MenuItem,
	Button,
	Switch,
} from "@material-ui/core";
import {
	SubscriptionSummary,
	TwilioEntryConfiguration,
	TwilioSubscriptionAdditionalConfiguration,
} from "../../../models/Subscription";
import { getSubscriptionDetails, getSubscriptionListByCLELoggingType } from "../../../services/api/subscription.api";
import { ENVIRONMENTS } from "../../../constants";
import { SelectorDisplayItemProps, Selector } from "../../Selector";
import { tryJsonParse, parsePositiveIntWithMaxLength } from "../../../utils";
import { SubscriptionSelector } from "./SubscriptionSelector";
import { cleLogger } from "@prftesp/cle-logger";
import { showSnack } from "../../../utils/component";
import { useSnackbar } from "notistack";

interface CLELoggerTestingFormProps {
	//The Id of the product.
	productId: string;
	//The URI for the external logger.
	externalLoggerURI: string;
	//The cle logging type to get the subscriptions for.
	cleType: string;
}

const useStyles = makeStyles((theme) => ({
	subscriptionList: {
		marginRight: theme.spacing(2),
	},
	form: {
		boxShadow: theme.shadows[1],
		marginTop: theme.spacing(1),
	},
	formControl: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		marginTop: "9px",
		display: "flex",
	},
	formControlLabel: {
		fontSize: theme.typography.caption.fontSize,
		marginBottom: theme.spacing(1),
	},
	inputFieldLarge: {
		width: 320,
		marginTop: "11px",
	},
	inputFieldExtraLarge: {
		width: 650,
		marginTop: "11px",
	},
	inputFieldSmall: {
		width: 120,
		marginTop: "4px",
	},
	progress: {
		margin: theme.spacing(2),
	},
	buttons: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: ".15em",
		justifyContent: "space-between",
		float: "right",
		marginRight: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
}));

/**
 * Component Displays the form and handles the state for the variables needed for sending a log to cle logger.
 */
export const CLELoggerTestingForm = (props: CLELoggerTestingFormProps) => {
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();

	//Fetched Data
	const [fetching, setFetching] = React.useState(false);
	const [subscriptions, setSubscriptions] = React.useState<SubscriptionSummary[] | undefined>(undefined);
	const [selectedSubscriptionId, setSelectedSubscriptionId] = React.useState<string | undefined>(undefined);
	const [environments, setEnvironments] = React.useState<SelectorDisplayItemProps[] | undefined>(undefined);
	const [selectedEnvironment, setSelectedEnvironment] = React.useState<string | undefined>(undefined);

	//Form Data
	const [message, setMessage] = React.useState("");
	const [logLevel, setLogLevel] = React.useState("verbose");
	const [additionalData, setAdditionalData] = React.useState<string>("");
	const [customPropertiesString, setCustomPropertiesString] = React.useState("");
	const [customPropertiesError, setCustomPropertiesError] = React.useState("");
	const [logCode, setLogCode] = React.useState(0);
	const [componentName, setComponentName] = React.useState("");
	const [correlationToken, setCorrelationToken] = React.useState("");
	const [piiSafe, setPiiSafe] = React.useState(false);

	const enableSubmission = message !== "";

	const handleSubmit = () => {
		let submit = true;
		let customProperties = {};
		if (customPropertiesString.length > 0) {
			try {
				customProperties = JSON.parse(customPropertiesString);
			} catch (error: any) {
				showSnack(enqueueSnackbar, `Error occurred while parsing custom properties field: ${error.message}`, "error");
				setCustomPropertiesError(error.message);
				submit = false;
			}
		}
		if (submit) {
			cleLogger.init({
				accountSid: selectedEnvironment,
				logSinks: [
					{
						name: "remote logger",
						log: cleLogger.remoteLogger(props.externalLoggerURI),
						logLevel: cleLogger.logLevels.VERBOSE,
						piiSafe: piiSafe,
					},
					{
						name: "remote logger",
						log: cleLogger.consoleLogger,
						logLevel: cleLogger.logLevels.VERBOSE,
						piiSafe: piiSafe,
					},
				],
			});
			cleLogger[logLevel](
				message,
				additionalData,
				customProperties,
				componentName,
				logCode === 0 ? null : logCode,
				correlationToken.length > 0 ? correlationToken : null,
				piiSafe,
			);
			showSnack(enqueueSnackbar, "Message was successfully sent to the logger.", "success");
		}
	};

	const handleSubscriptionIdSet = (subscriptionId: string) => {
		setSelectedSubscriptionId(subscriptionId);
		setEnvironments(undefined);
		setSelectedEnvironment(undefined);
	};

	const getSubscriptions = (productId: string, cleType: string) => {
		const getSubscriptionsAsync = async (productId: string, cleType: string) => {
			const response = getSubscriptionListByCLELoggingType(productId, cleType)();
			setSubscriptions((await response).data);
			setFetching(false);
		};
		if (subscriptions === undefined && !fetching) {
			getSubscriptionsAsync(productId, cleType);
			setFetching(true);
		}
		return <CircularProgress data-testid="subscriptions-loading-indicator" className={classes.progress} />;
	};

	const getEnvironments = (productId: string) => {
		const getEnvironmentsAsync = async (productId: string) => {
			const response = getSubscriptionDetails(productId, selectedSubscriptionId!)();
			const newEnvironments: TwilioSubscriptionAdditionalConfiguration = tryJsonParse(
				(await response).data.subscription!.subscriptionAdditionalConfiguration,
				[],
			);
			setEnvironments(
				newEnvironments.SubscriptionConfigurations.map((e: TwilioEntryConfiguration) => ({
					displayString: ENVIRONMENTS[e.Environment].text,
					valueString: e.AccountSid,
				})),
			);
			setSelectedEnvironment(newEnvironments.SubscriptionConfigurations[0].AccountSid);
			setFetching(false);
		};
		if (environments === undefined && !fetching) {
			getEnvironmentsAsync(productId);
			setFetching(true);
		}
		return <CircularProgress data-testid="environments-loading-indicator" className={classes.progress} />;
	};

	return (
		<div style={{ display: "flex", flexWrap: "wrap" }}>
			<div className={classes.subscriptionList}>
				{subscriptions ? (
					<SubscriptionSelector
						subscriptions={subscriptions}
						setSubscription={handleSubscriptionIdSet}
						selectedSubscriptionId={selectedSubscriptionId}
						productId={props.productId}
					/>
				) : (
					getSubscriptions(props.productId, props.cleType)
				)}
			</div>
			{selectedSubscriptionId &&
				(environments ? (
					<div className={classes.form} data-testid={"cle-logger-testing-input-form"}>
						<div>
							<FormControl className={classes.formControl}>
								<div style={{ display: "flex" }}>
									<Selector
										items={environments}
										updateSelected={setSelectedEnvironment}
										label={"Environments"}
										initialValue={selectedEnvironment || environments[0].valueString!}
									/>
									<div style={{ marginLeft: 10 }}>
										<FormLabel id={"log-level-id"} className={classes.formControlLabel}>
											Log Level
										</FormLabel>
										<div>
											<Select
												labelId="log-level-id"
												id="log-level-select"
												value={logLevel}
												onChange={(e) => setLogLevel(e.target.value as string)}
												className={classes.inputFieldSmall}
											>
												<MenuItem value={"critical"}>Critical</MenuItem>
												<MenuItem value={"error"}>Error</MenuItem>
												<MenuItem value={"warn"}>Warning</MenuItem>
												<MenuItem value={"info"}>Info</MenuItem>
												<MenuItem value={"verbose"}>Verbose</MenuItem>
											</Select>
										</div>
									</div>
									<div style={{ marginLeft: 20 }}>
										<FormLabel className={classes.formControlLabel}>Log Code (Optional)</FormLabel>
										<div>
											<TextField
												type="number"
												className={classes.inputFieldSmall}
												inputProps={{ min: "0" }}
												value={logCode}
												margin="normal"
												onChange={(e) => {
													setLogCode(parsePositiveIntWithMaxLength(e.target.value, 9));
												}}
											/>
										</div>
									</div>
									<div style={{ marginLeft: 10 }}>
										<FormLabel className={classes.formControlLabel} style={{ marginLeft: 10 }}>
											PII Safe
										</FormLabel>
										<div>
											<Switch
												checked={piiSafe}
												onChange={(e) => setPiiSafe(e.target.checked)}
												name="Pii Safe"
												color="primary"
											/>
										</div>
									</div>
								</div>
								<div style={{ display: "flex", marginLeft: 7 }}>
									<div>
										<FormLabel className={classes.formControlLabel}>Component Name (Optional)</FormLabel>
										<div>
											<TextField
												className={classes.inputFieldLarge}
												value={componentName}
												margin="normal"
												onChange={(e) => setComponentName(e.target.value)}
											/>
										</div>
									</div>
									<div style={{ marginLeft: 10 }}>
										<FormLabel className={classes.formControlLabel}>Correlation Token (Optional)</FormLabel>
										<div>
											<TextField
												className={classes.inputFieldLarge}
												value={correlationToken}
												margin="normal"
												onChange={(e) => setCorrelationToken(e.target.value)}
											/>
										</div>
									</div>
								</div>
								<div style={{ display: "flex", marginLeft: 7 }}>
									<div>
										<FormLabel className={classes.formControlLabel}>Additional Data (Optional)</FormLabel>
										<div>
											<TextField
												className={classes.inputFieldLarge}
												value={additionalData}
												margin="normal"
												multiline={true}
												rowsMax={8}
												onChange={(e) => setAdditionalData(e.target.value)}
											/>
										</div>
									</div>
									<div style={{ marginLeft: 10 }}>
										<FormLabel className={classes.formControlLabel} error={customPropertiesError.length > 0}>
											Custom Properties (Optional JSON)
										</FormLabel>
										<div>
											<TextField
												className={classes.inputFieldLarge}
												value={customPropertiesString}
												margin="normal"
												multiline={true}
												rowsMax={8}
												onChange={(e) => {
													setCustomPropertiesError("");
													setCustomPropertiesString(e.target.value);
												}}
												error={customPropertiesError.length > 0}
												helperText={customPropertiesError}
											/>
										</div>
									</div>
								</div>
								<div style={{ marginLeft: 7, marginRight: 7 }}>
									<FormLabel className={classes.formControlLabel}>Message</FormLabel>
									<div>
										<TextField
											className={classes.inputFieldExtraLarge}
											value={message}
											margin="normal"
											multiline={true}
											rowsMax={8}
											onChange={(e) => setMessage(e.target.value)}
										/>
									</div>
								</div>
							</FormControl>
						</div>
						<div>
							<Button
								size="large"
								variant="contained"
								color={"primary"}
								onClick={handleSubmit}
								className={classes.buttons}
								data-testid="log-submit-button"
								disabled={!enableSubmission}
							>
								{"Submit"}
							</Button>
						</div>
					</div>
				) : (
					getEnvironments(props.productId)
				))}
		</div>
	);
};
